/**
 * OnboardingContext — manages per-mode onboarding overlay state for the PlanViewer.
 *
 * Responsibilities:
 * - Tracks whether the onboarding overlay is open, and for which mode.
 * - Provides `openOnboarding(modeId)` / `closeOnboarding()` for imperative control
 *   from any component in the product domain (e.g. a deep button trigger).
 * - On first mount and on every `activeMode` change, auto-opens the onboarding
 *   if sessionStorage has no record of the user having seen it for that mode.
 *
 * SessionStorage keys are namespaced per mode via the ONBOARDING_SESSION_KEY helper.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'

// ── Storage helpers ───────────────────────────────────────────────────────────

/**
 * Returns the sessionStorage key for a given mode's onboarding seen-flag.
 * @param {string} modeId
 * @returns {string}
 */
export function onboardingSessionKey(modeId) {
  return `bim:onboarding_seen:${modeId}`
}

function _readSeen(modeId) {
  try {
    return globalThis.sessionStorage?.getItem(onboardingSessionKey(modeId)) === '1'
  } catch {
    return true // sessionStorage unavailable → don't nag
  }
}

/** Writes '0' the first time a mode is encountered — key becomes visible in DevTools. */
function _writeInitial(modeId) {
  try {
    const ss = globalThis.sessionStorage
    if (ss && ss.getItem(onboardingSessionKey(modeId)) === null) {
      ss.setItem(onboardingSessionKey(modeId), '0')
    }
  } catch { /* ignore */ }
}

function _writeSeen(modeId) {
  try {
    globalThis.sessionStorage?.setItem(onboardingSessionKey(modeId), '1')
  } catch {
    /* ignore quota / privacy mode */
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

const OnboardingContext = createContext(undefined)

const NULL_ONBOARDING_CONTEXT = {
  isOpen: false,
  activeModeId: null,
  openOnboarding: () => {},
  closeOnboarding: () => {},
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * OnboardingContextProvider — place inside ProductContextProvider, but outside PlanViewer.
 *
 * @param {{ children: React.ReactNode, activeMode: string|null }} props
 */
export function OnboardingContextProvider({ children, activeMode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeModeId, setActiveModeId] = useState(null)

  // Auto-open on first visit to a mode (per session).
  useEffect(() => {
    if (!activeMode) return
    if (!_readSeen(activeMode)) {
      _writeInitial(activeMode)  // write '0' so key is visible in DevTools immediately
      setActiveModeId(activeMode)
      setIsOpen(true)
    } else {
      // Mode changed and already seen — close any open onboarding immediately.
      setIsOpen(false)
    }
  }, [activeMode])

  const openOnboarding = useCallback((modeId) => {
    setActiveModeId(modeId)
    setIsOpen(true)
  }, [])

  const closeOnboarding = useCallback(() => {
    if (activeModeId) _writeSeen(activeModeId)
    setIsOpen(false)
  }, [activeModeId])

  return (
    <OnboardingContext.Provider value={{ isOpen, activeModeId, openOnboarding, closeOnboarding }}>
      {children}
    </OnboardingContext.Provider>
  )
}

// ── Consumer hook ─────────────────────────────────────────────────────────────

/**
 * useOnboarding — reads/controls the onboarding overlay.
 *
 * Must be called within OnboardingContextProvider.
 * Falls back to no-op context so that components outside a provider are safe.
 */
export function useOnboarding() {
  return useContext(OnboardingContext) ?? NULL_ONBOARDING_CONTEXT
}
